import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, Link2, Play, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiError, formatDate } from "@/lib/api";
import { ConfirmButton } from "@/components/ConfirmButton";
import { AssignEmployeeSelect } from "@/components/contracts/AssignEmployeeSelect";

export const SyncStatusCard = ({ status, unmatched, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const resume = async () => {
    setBusy(true);
    try { await api.post("/contracts/sync"); toast.success("Lettura ripresa: continua da dove era arrivata"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const stop = async () => {
    setBusy(true);
    try { await api.post("/contracts/sync/cancel"); toast.success("Lettura interrotta: potrai riprenderla"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (status.status === "never" && unmatched.length === 0) {
    return (
      <div className="panel flex items-start gap-3 p-4 text-sm text-slate-600" data-testid="sync-status-never">
        <Link2 className="mt-0.5 h-4 w-4 shrink-0" />
        <p>Nessuna lettura effettuata. Configura la casella in <b>Impostazioni → Casella di posta (IMAP)</b> e premi <b>Leggi dalla casella</b>: cerco gli allegati PDF/DOCX degli ultimi mesi, li analizzo e li abbino ai dipendenti.</p>
      </div>
    );
  }
  const running = status.status === "running";
  const Icon = running ? Loader2 : status.status === "error" ? AlertTriangle : CheckCircle2;
  return (
    <div className="space-y-3">
      {status.status !== "never" && (
        <div className={`panel p-4 text-sm ${status.status === "error" ? "border-rose-200" : ""}`} data-testid={`sync-status-${status.status}`}>
          <div className="flex flex-wrap items-center gap-2">
            <Icon className={`h-4 w-4 ${running ? "animate-spin text-slate-500" : status.status === "error" ? "text-rose-600" : "text-emerald-600"}`} />
            <p className="font-medium text-slate-900">
              {running ? `Lettura in corso… ${status.progress || ""}` : status.status === "error" ? (status.interrupted ? "Lettura interrotta" : "Lettura fermata con errore") : `Ultima lettura: ${formatDate(status.finished_at)}`}
            </p>
            {running && <Button size="sm" variant="outline" disabled={busy} onClick={stop} data-testid="sync-cancel-button"><Square className="mr-1 h-3.5 w-3.5" /> Interrompi</Button>}
            {status.status === "error" && <Button size="sm" disabled={busy} onClick={resume} data-testid="resume-sync-button"><Play className="mr-1 h-3.5 w-3.5" /> Riprendi da dove era</Button>}
            <span className="ml-auto font-mono text-xs text-slate-500" data-testid="sync-counters">
              {status.emails_new ?? 0} email nuove · {status.found ?? 0} allegati · {status.imported ?? 0} importati · {status.skipped ?? 0} già presenti · {status.discarded ?? 0} scartati · {status.new_employees ?? 0} nuovi dipendenti
            </span>
          </div>
          {status.errors?.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-rose-700" data-testid="sync-errors">{status.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}</ul>
          )}
          {status.log?.length > 0 && (
            <>
              <button type="button" className="mt-2 text-xs text-slate-500 underline" onClick={() => setOpen((o) => !o)} data-testid="sync-log-toggle">{open ? "Nascondi dettagli" : "Mostra dettagli"}</button>
              {open && <ul className="mt-1 max-h-40 space-y-0.5 overflow-auto font-mono text-[11px] text-slate-600">{status.log.map((l, i) => <li key={i}>{l}</li>)}</ul>}
            </>
          )}
        </div>
      )}
      {unmatched.length > 0 && <UnmatchedList items={unmatched} onChanged={onChanged} />}
    </div>
  );
};

const UnmatchedList = ({ items, onChanged }) => {
  const remove = async (c) => {
    try { await api.delete(`/contracts/${c.id}`); toast.success("Documento eliminato"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="panel border-amber-200 p-4 text-sm" data-testid="unmatched-contracts">
      <p className="font-medium text-amber-900">{items.length} documenti senza dipendente riconosciuto</p>
      <p className="text-xs text-amber-800">Scegli il dipendente giusto dal menu, oppure elimina il documento.</p>
      <ul className="mt-2 space-y-2">
        {items.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600" data-testid={`unmatched-item-${c.id}`}>
            <span className="min-w-0 flex-1 truncate">{c.filename || "documento"} · {[c.cognome, c.nome].filter(Boolean).join(" ") || "nome non letto"}{c.codice_fiscale ? ` · ${c.codice_fiscale}` : ""} · {c.email_subject}</span>
            <div className="flex items-center gap-1">
              <AssignEmployeeSelect contract={c} onAssigned={onChanged} />
              <ConfirmButton testId={`delete-unmatched-${c.id}`} size="sm" title="Eliminare il documento?" onConfirm={() => remove(c)}>Elimina</ConfirmButton>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
